import type { MenuItem, ModificationGroup } from "@/features/menu/menuSlice";
import { requiredCount } from "@/types/preorderV2";
import { hydrateFromOrder } from "./cartSlice";
import type {
  BackendModifierPayload,
  CartHydrationMode,
  CartModifierGroup,
  BackendCartItem,
} from "./cartSlice";

export interface OrderToCartMenuLookup {
  items: MenuItem[];
  itemsById: Record<string, MenuItem>;
  modificationsById: Record<string, ModificationGroup>;
  /**
   * FOYR-669: stored guids of items not served only because they are out of
   * stock or offline. Absent means the server has not said (see
   * resolveStoredLine); `[]` means nothing is temporarily unavailable.
   */
  unavailableItemIds?: readonly string[];
  /** FOYR-669: the same for modifier options on a served item. */
  unavailableOptionIds?: readonly string[];
}

/** A line with no name to show: never its raw id. */
const FALLBACK_ITEM_NAME = "Item";

/** An option taken off a kept line. "" when it was stored with no name. */
export interface LeftOutOption {
  readonly itemName: string;
  readonly optionName: string;
}

/** What a stored order holds that the guest no longer sees (FOYR-669). */
export interface OrderLeftOut {
  /** Names of the lines left out; "" for one stored with no name. */
  readonly items: readonly string[];
  readonly options: readonly LeftOutOption[];
}

export interface ResolvedOrder {
  readonly lines: CartAddItemPayload[];
  readonly leftOut: OrderLeftOut;
}

export interface CartAddItemPayload {
  menuItemId: string;
  name: string;
  basePriceCents: number;
  quantity: number;
  imageUrl?: string;
  caloriesLabel?: string;
  modifiers?: CartModifierGroup[];
  backendItem: BackendCartItem;
  /** FOYR-669: out of stock or offline for now; stays in the order. */
  unavailable?: boolean;
}

/**
 * Stored guids against the menu's, ignoring case as the save does: a POS may
 * change an id's case, and a miss here hides a line the save would keep.
 */
const sameGuid = (a: string, b: string | undefined): boolean =>
  b !== undefined && a.toLowerCase() === b.toLowerCase();

/** A stored modifier, resolved to the group and option the menu serves now. */
interface ResolvedOption {
  readonly groupId: string;
  readonly groupName: string;
  readonly optionId: string;
  readonly optionName: string;
  readonly priceCents: number;
}

/**
 * A stored modifier (`optionGroup.guid` + `item.guid`) against what the item
 * serves now. An item's own V2 groups (FOYR-565), even none, are the answer:
 * an option's `priceCents` is the value the backend prices it at, and an
 * option missing from them is not served, even if a legacy group or another
 * item's group lists it (the backend looks only at this item). Only an item
 * from a menu persisted before V2 falls back to the legacy groups. Null when
 * not served.
 */
function resolveStoredOption(
  menuItem: MenuItem,
  menu: OrderToCartMenuLookup,
  groupGuid: string,
  optionGuid: string,
): ResolvedOption | null {
  const v2Groups = menuItem.modifierGroups;
  if (v2Groups) {
    const v2Group = v2Groups.find((g) => sameGuid(groupGuid, g._id));
    const v2Option = v2Group?.options.find((o) => sameGuid(optionGuid, o._id));
    if (!v2Group || !v2Option) return null;
    return {
      groupId: v2Group._id,
      groupName: v2Group.name,
      optionId: v2Option._id,
      optionName: v2Option.name,
      priceCents: v2Option.priceCents,
    };
  }
  const legacy = menu.modificationsById[groupGuid];
  const opt = legacy?.data?.find(
    (o) => o.posId === optionGuid || o.id === optionGuid,
  );
  if (!legacy || !opt) return null;
  return {
    groupId: legacy.mod_id,
    groupName: legacy.displayName ?? legacy.name ?? "",
    optionId: opt.id,
    optionName: opt.displayName,
    priceCents: Number(opt.prices?.[0]?.price ?? 0),
  };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/** `value[key]` as a guid: stored guids are strings, older ones may be numbers. */
const guidAt = (value: unknown, key: string): string => {
  const field = isRecord(value) ? value[key] : undefined;
  return field === undefined || field === null ? "" : String(field);
};

const nameAt = (value: unknown, key: string): string => {
  const field = isRecord(value) ? value[key] : undefined;
  return typeof field === "string" ? field : "";
};

const quantityOf = (value: unknown): number => {
  const field = isRecord(value) ? value.quantity : undefined;
  return Number(field ?? 1);
};

/** One stored modifier, read defensively: the order is untrusted JSON. */
interface StoredModifier {
  readonly groupGuid: string;
  readonly groupName: string;
  readonly optionGuid: string;
  readonly optionName: string;
  readonly quantity: number;
  /** FOYR-669: what it cost when saved; on the wire only for a send-locked ticket. */
  readonly savedPriceCents: number | null;
  readonly nested: BackendModifierPayload[];
}

interface StoredLine {
  readonly guid: string;
  /** The name it was stored with; "" when absent or only ever the raw guid. */
  readonly name: string;
  readonly itemGroupGuid: string;
  readonly quantity: number;
  /** FOYR-669: what it cost when saved; on the wire only for a send-locked ticket. */
  readonly savedPriceCents: number | null;
  readonly modifiers: readonly StoredModifier[];
}

/** A saved price is integer cents, 0 included; anything else is no price. */
const savedPriceOf = (value: unknown): number | null => {
  const field = isRecord(value) ? value.unitPriceCents : undefined;
  return typeof field === "number" && Number.isInteger(field) && field >= 0
    ? field
    : null;
};

/** A modifier with no option guid names nothing, and the server refuses it. */
const toStoredModifiers = (raw: unknown): StoredModifier[] => {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((modifier: unknown) => {
    const group = isRecord(modifier) ? modifier.optionGroup : undefined;
    const option = isRecord(modifier) ? modifier.item : undefined;
    const optionGuid = guidAt(option, "guid");
    if (!optionGuid) return [];
    return [
      {
        groupGuid: guidAt(group, "guid"),
        groupName: nameAt(group, "name"),
        optionGuid,
        optionName: nameAt(option, "name"),
        quantity: quantityOf(modifier),
        savedPriceCents: savedPriceOf(modifier),
        nested: toStoredModifiers(
          isRecord(modifier) ? modifier.modifiers : undefined,
        ).map((nested) => toBackendModifier(nested, null, null)),
      },
    ];
  });
};

function toStoredLine(raw: unknown): StoredLine {
  const item = isRecord(raw) ? raw.item : undefined;
  const guid = guidAt(item, "guid");
  const name = nameAt(item, "name").trim();
  return {
    guid,
    // An earlier hydration fell back to the guid as the name and a save
    // wrote it back: that is an id, not a name.
    name: name === guid ? "" : name,
    itemGroupGuid: guidAt(isRecord(raw) ? raw.itemGroup : undefined, "guid"),
    quantity: quantityOf(raw),
    savedPriceCents: savedPriceOf(raw),
    modifiers: toStoredModifiers(isRecord(raw) ? raw.modifiers : undefined),
  };
}

/**
 * The modifier line a save sends back: the stored guids, always, with the
 * names the menu serves now when it does.
 */
function toBackendModifier(
  stored: StoredModifier,
  match: ResolvedOption | null,
  menu: OrderToCartMenuLookup | null,
): BackendModifierPayload {
  const group = menu?.modificationsById[stored.groupGuid];
  return {
    optionGroup: {
      guid: stored.groupGuid,
      name:
        match?.groupName ?? group?.displayName ?? group?.name ?? stored.groupName,
    },
    item: {
      guid: stored.optionGuid,
      name: match?.optionName ?? stored.optionName,
    },
    quantity: stored.quantity,
    modifiers: stored.nested,
  };
}

/** A line's options as cart groups, priced where the menu still serves them. */
function toCartGroups(
  judged: readonly JudgedModifier[],
): CartModifierGroup[] {
  return judged.reduce<CartModifierGroup[]>((groups, { stored, match, unavailable }) => {
    const groupId = match?.groupId ?? stored.groupGuid;
    const option = {
      id: match?.optionId ?? stored.optionGuid,
      displayName: match?.optionName ?? stored.optionName,
      priceCents: match?.priceCents ?? 0,
      quantity: stored.quantity,
      ...(unavailable ? { unavailable: true } : {}),
    };
    const existing = groups.find((g) => g.mod_id === groupId);
    if (existing) {
      return groups.map((g) =>
        g === existing ? { ...g, options: [...g.options, option] } : g,
      );
    }
    return [
      ...groups,
      {
        mod_id: groupId,
        displayName: match?.groupName ?? stored.groupName,
        options: [option],
      },
    ];
  }, []);
}

interface JudgedModifier {
  readonly stored: StoredModifier;
  readonly match: ResolvedOption | null;
  /** Out of stock or offline for now: stays on the line, sent back unchanged. */
  readonly unavailable: boolean;
}

/**
 * A line whose item the menu does not serve: sent back as stored, with no
 * price (nothing on the wire says what it costs) and its stored names.
 */
function unservedLine(stored: StoredLine, unavailable: boolean): CartAddItemPayload {
  const judged = stored.modifiers.map((modifier) => ({
    stored: modifier,
    match: null,
    unavailable: false,
  }));
  const groups = toCartGroups(judged);
  return {
    menuItemId: stored.guid,
    name: stored.name || FALLBACK_ITEM_NAME,
    basePriceCents: 0,
    quantity: stored.quantity,
    modifiers: groups.length ? groups : undefined,
    ...(unavailable ? { unavailable: true } : {}),
    backendItem: {
      itemGroup: stored.itemGroupGuid
        ? { guid: stored.itemGroupGuid }
        : undefined,
      item: {
        guid: stored.guid,
        ...(stored.name ? { name: stored.name } : {}),
      },
      quantity: stored.quantity,
      modifiers: stored.modifiers
        .filter((modifier) => modifier.groupGuid)
        .map((modifier) => toBackendModifier(modifier, null, null)),
    },
  };
}

function servedLine(
  stored: StoredLine,
  menuItem: MenuItem,
  judged: readonly JudgedModifier[],
  menu: OrderToCartMenuLookup,
): CartAddItemPayload {
  const name = menuItem.name || stored.name || FALLBACK_ITEM_NAME;
  const groups = toCartGroups(judged);
  const itemGroupGuid = menuItem.posCategoryIds?.[0];
  return {
    // The menu's own id, not the stored guid: the stored guid may be the
    // `_id` while menu items (and every line added from the menu) key on
    // `id`. Keying on the stored guid would split one item into two cart
    // lines and hide it from the menu's quantity badges.
    menuItemId: menuItem.id,
    name,
    basePriceCents: menuItem.priceCents || 0,
    quantity: stored.quantity,
    imageUrl: menuItem.imageUrl,
    caloriesLabel: menuItem.caloriesLabel,
    modifiers: groups.length ? groups : undefined,
    backendItem: {
      itemGroup: itemGroupGuid ? { guid: String(itemGroupGuid) } : undefined,
      // FOYR-424 R15: the line identity is always the menu item's `_id`, so
      // an older order stored under POS ids is rewritten on its next edit. A
      // menu persisted before `menuItemId` existed keeps the stored guid, so
      // a replacing PUT sends back what is stored rather than "undefined".
      item: {
        guid: menuItem.menuItemId || stored.guid,
        name,
      },
      quantity: stored.quantity,
      modifiers: judged.map(({ stored: modifier, match }) =>
        toBackendModifier(modifier, match, menu),
      ),
    },
  };
}

type ResolvedLine =
  | {
      readonly kept: true;
      readonly line: CartAddItemPayload;
      readonly droppedOptions: readonly LeftOutOption[];
    }
  | { readonly kept: false; readonly name: string };

function findMenuItem(
  guid: string,
  menu: OrderToCartMenuLookup,
): MenuItem | undefined {
  return (
    menu.itemsById[guid] ||
    menu.items.find(
      (m) =>
        sameGuid(guid, m.id) ||
        sameGuid(guid, m.menuItemId) ||
        sameGuid(guid, m.posItemId),
    )
  );
}

/**
 * What a stored line is against the menu served now (FOYR-669), the same
 * judgement the save makes:
 *
 * - its item is served: kept. An unserved option the server lists as
 *   temporarily unavailable stays, unchanged and marked, and counts toward
 *   its group's rule. One not listed is gone: dropped and named, and if that
 *   leaves its group short of what the rule needs, the whole line goes. So
 *   does a line short of a required group with nothing left to choose from,
 *   whatever it selected. A required group the guest never chose from, that
 *   still has options, is not this case: the server refuses it and Place asks
 *   for a choice.
 * - its item is not served, and the server lists it as temporarily
 *   unavailable: kept as it was stored, marked unavailable.
 * - its item is not served and not listed: gone for good, left out.
 *
 * With no list (a backend that does not send one, or a menu not yet loaded)
 * nothing here can tell gone from out of stock, so the line or option is kept
 * and marked: hiding it would remove it on the next replacing PUT.
 *
 * An option stored with no group guid can't go back at all: the save refuses
 * an empty guid outright, so it comes off with the notice, list or no list.
 */
function resolveStoredLine(
  stored: StoredLine,
  menu: OrderToCartMenuLookup,
  judge: boolean,
): ResolvedLine {
  // A stored guid is the menu item's `_id` (written since FOYR-424) or, for
  // an older order, its POS id; itemsById is indexed by both.
  const menuItem = findMenuItem(stored.guid, menu);

  if (!menuItem) {
    if (!judge) return { kept: true, line: unservedLine(stored, false), droppedOptions: [] };
    const known = menu.unavailableItemIds;
    if (known && !known.some((id) => sameGuid(stored.guid, id))) {
      return { kept: false, name: stored.name };
    }
    return { kept: true, line: unservedLine(stored, true), droppedOptions: [] };
  }

  // Only an item that states its groups can say an option is gone; one from
  // a menu persisted before V2 has no answer yet.
  const knownOptions =
    judge && menuItem.modifierGroups !== undefined
      ? menu.unavailableOptionIds
      : undefined;
  const judgedAll = stored.modifiers.map((modifier) => {
    const match = resolveStoredOption(
      menuItem,
      menu,
      modifier.groupGuid,
      modifier.optionGuid,
    );
    const unserved = judge && match === null;
    const gone =
      unserved &&
      (!modifier.groupGuid ||
        (knownOptions !== undefined &&
          !knownOptions.some((id) => sameGuid(modifier.optionGuid, id))));
    return {
      gone,
      judged: { stored: modifier, match, unavailable: unserved && !gone },
    };
  });
  const dropped = judgedAll.filter((j) => j.gone).map((j) => j.judged);
  const kept = judgedAll.filter((j) => !j.gone).map((j) => j.judged);
  const droppedFrom = new Set(
    dropped.map((j) => j.stored.groupGuid.toLowerCase()),
  );
  const ruleBroken = (menuItem.modifierGroups ?? []).some(
    (group) =>
      (droppedFrom.has(group._id.toLowerCase()) ||
        (knownOptions !== undefined && group.options.length === 0)) &&
      kept.filter((j) => sameGuid(j.stored.groupGuid, group._id)).length <
        requiredCount(group),
  );
  const name = menuItem.name || stored.name;
  if (ruleBroken) return { kept: false, name };
  return {
    kept: true,
    line: servedLine(stored, menuItem, kept, menu),
    droppedOptions: dropped.map((j) => ({
      itemName: name,
      optionName: j.stored.optionName.trim(),
    })),
  };
}

/**
 * One stored line of a send-locked ticket; `priced` is false when it or an option
 * on it was saved with no price, so its amount is unknown, as on the merchant list.
 */
export interface PlacedLine {
  readonly line: CartAddItemPayload;
  readonly priced: boolean;
}

/**
 * The order as it went to the POS (FOYR-669, Dev 2026-11-03: "once sent ... we
 * should not modify the preorder"): stored names and saved prices, with no menu
 * read, so a later rename, price change or delete can't touch it. `null` when no
 * line or option carries a price at all: a backend that does not serve them yet,
 * so the caller reads the menu as before rather than show a blank total.
 */
export function placedOrderLines(orderItems: readonly unknown[]): PlacedLine[] | null {
  const stored = orderItems.map(toStoredLine);
  const anyPrice = stored.some(
    (line) =>
      line.savedPriceCents !== null ||
      line.modifiers.some((modifier) => modifier.savedPriceCents !== null),
  );
  if (!anyPrice) return null;
  return stored.map((line) => {
    const judged = line.modifiers.map((modifier) => ({
      stored: modifier,
      match: {
        groupId: modifier.groupGuid,
        groupName: modifier.groupName,
        optionId: modifier.optionGuid,
        optionName: modifier.optionName,
        priceCents: modifier.savedPriceCents ?? 0,
      },
      unavailable: false,
    }));
    const groups = toCartGroups(judged);
    return {
      priced:
        line.savedPriceCents !== null &&
        line.modifiers.every((modifier) => modifier.savedPriceCents !== null),
      line: {
        menuItemId: line.guid,
        name: line.name || FALLBACK_ITEM_NAME,
        basePriceCents: line.savedPriceCents ?? 0,
        quantity: line.quantity,
        modifiers: groups.length ? groups : undefined,
        backendItem: {
          item: { guid: line.guid, ...(line.name ? { name: line.name } : {}) },
          quantity: line.quantity,
          modifiers: [],
        },
      },
    };
  });
}

export interface ResolveOptions {
  /**
   * False reads the order as it was placed, judging nothing: for a locked
   * ticket, where no save can follow and the kitchen may already have it.
   */
  readonly judge?: boolean;
}

/**
 * The stored order against the current menu: the lines to show and send, and
 * what was left out (FOYR-669). The one derivation behind page load, "Edit
 * Order", the replacing-PUT guard and the read-only ticket, so what the guest
 * sees and what a save sends never diverge. A left-out line stays in the
 * stored order until the next save, which replaces it wholesale with the
 * cart; nothing writes on its own.
 */
export function resolveStoredOrder(
  orderItems: unknown[],
  menu: OrderToCartMenuLookup,
  { judge = true }: ResolveOptions = {},
): ResolvedOrder {
  const items = Array.isArray(orderItems) ? orderItems : [];
  const resolved = items.map((raw) =>
    resolveStoredLine(toStoredLine(raw), menu, judge),
  );
  return {
    lines: resolved.flatMap((r) => (r.kept ? [r.line] : [])),
    leftOut: {
      items: resolved.flatMap((r) => (r.kept ? [] : [r.name])),
      options: resolved.flatMap((r) => (r.kept ? r.droppedOptions : [])),
    },
  };
}

/** Cart payloads for what a stored order still holds (see resolveStoredOrder). */
export function buildCartItemsFromOrder(
  orderItems: unknown[],
  menu: OrderToCartMenuLookup,
): CartAddItemPayload[] {
  return resolveStoredOrder(orderItems, menu).lines;
}

/**
 * Fingerprint of a stored order's items. The cart records the key of the
 * order it was hydrated from; a cart whose key doesn't match the
 * reservation's current order does not hold that order (FOYR-469).
 */
export function orderSourceKey(orderItems: readonly unknown[]): string {
  return JSON.stringify(orderItems);
}

/** Whether a cart holding `cartKey` was hydrated from exactly these stored items. */
export function isCartHydratedFromOrder(
  cartKey: string | undefined,
  orderItems: readonly unknown[],
): boolean {
  return cartKey === orderSourceKey(orderItems);
}

/**
 * The cart action that hydrates from a stored order - the one path shared
 * by page load, "Edit Order", "Add more" and the replacing-PUT guard, all
 * derived through buildCartItemsFromOrder.
 */
export function hydrateCartFromOrder(
  orderItems: unknown[],
  menu: OrderToCartMenuLookup,
  mode: CartHydrationMode,
) {
  return hydrateFromOrder({
    items: buildCartItemsFromOrder(orderItems, menu),
    sourceKey: orderSourceKey(orderItems),
    mode,
  });
}
